'use client';

import {
  AlignCenterHorizontal,
  AlignCenterVertical,
  AlignEndHorizontal,
  AlignEndVertical,
  AlignHorizontalDistributeCenter,
  AlignStartHorizontal,
  AlignStartVertical,
  AlignVerticalDistributeCenter,
  ArrowDownFromLine,
  ArrowUpFromLine,
  Blocks,
  BringToFront,
  ClipboardPaste,
  Copy,
  Crosshair,
  ExternalLink,
  Files,
  Frame,
  Heading,
  Magnet,
  StickyNote,
  MessageSquarePlus,
  Maximize,
  MoreHorizontal,
  Pin,
  PinOff,
  Plus,
  Scissors,
  Search,
  SendToBack,
  Share2,
  Trash2,
  type LucideIcon,
} from 'lucide-react';
import type { CommandIconId } from '@/lib/workspace/commands';

/**
 * Single mapping from command icon ids → lucide React components.
 *
 * Centralising the lookup here keeps `lib/workspace/commands.ts`
 * dependency-free (it doesn't import React) so the registry stays
 * unit-testable in plain Node and the icon set can evolve without
 * touching command predicates.
 *
 * Adding a new icon: extend `CommandIconId` in `commands.ts` AND
 * add a row below. The TypeScript `Record<CommandIconId, …>` type
 * fails the build if either step is missed.
 */
const ICONS: Record<CommandIconId, LucideIcon> = {
  copy: Copy,
  cut: Scissors,
  paste: ClipboardPaste,
  // `Files` is lucide's stacked-rectangle glyph — the convention
  // every canvas tool (Figma, Sketch, Whimsical, Illustrator) uses
  // for Duplicate. Previously we reused `Copy` here, which made the
  // Copy and Duplicate menu rows visually indistinguishable.
  duplicate: Files,
  delete: Trash2,
  // Z-order icons map to lucide's dedicated `BringToFront` /
  // `SendToBack` glyphs (the small-square-on-top-of-big-square
  // stack metaphor). Forward / Backward single steps use the
  // `ArrowUp/DownFromLine` arrows which read as "move one layer
  // up / down" without conflicting with the chevron-based scroll
  // affordances used elsewhere in the chrome.
  'bring-to-front': BringToFront,
  'send-to-back': SendToBack,
  'bring-forward': ArrowUpFromLine,
  'send-backward': ArrowDownFromLine,
  // Object-align icons (NOT text-align). Lucide's `Align…Vertical`
  // family shows vertical bars anchored to a left/center/right
  // vertical guide — the correct picture for "align the LEFT edge
  // of every object to the leftmost edge of the bbox". The
  // `Align…Horizontal` family is the rotated equivalent for
  // top/middle/bottom.
  'align-left': AlignStartVertical,
  'align-center-h': AlignCenterVertical,
  'align-right': AlignEndVertical,
  'align-top': AlignStartHorizontal,
  'align-center-v': AlignCenterHorizontal,
  'align-bottom': AlignEndHorizontal,
  'distribute-h': AlignHorizontalDistributeCenter,
  'distribute-v': AlignVerticalDistributeCenter,
  focus: Crosshair,
  fit: Maximize,
  open: ExternalLink,
  share: Share2,
  pin: Pin,
  unpin: PinOff,
  'show-title': Heading,
  'hide-title': Heading,
  'new-chat': Plus,
  'paste-note': ClipboardPaste,
  note: StickyNote,
  board: Frame,
  comment: MessageSquarePlus,
  'add-widget': Blocks,
  search: Search,
  snap: Magnet,
  more: MoreHorizontal,
};

/**
 * Render the icon for a registry command id. Returns `null` when the
 * id is unknown (callers can render a placeholder span to keep the
 * row width consistent — see `canvas-context-menu.tsx`).
 */
export function CommandIcon({
  id,
  className,
}: {
  id: CommandIconId;
  className?: string;
}) {
  const Icon = ICONS[id];
  if (!Icon) return null;
  return <Icon className={className} aria-hidden />;
}
